import React, { useState, useEffect } from "react";
import { fetchProfile, updateProfile } from "@shared/api/authProfile";

export default function MyProfileModal({ isOpen, onClose, onOpenChangePassword }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({ given_name: "", family_name: "", phone_number: "" });

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setSuccess(null);
    setLoading(true);
    fetchProfile()
      .then((data) => {
        setProfile(data);
        setForm({
          given_name: data.given_name ?? "",
          family_name: data.family_name ?? "",
          phone_number: data.phone_number ?? "",
        });
      })
      .catch((err) => {
        setError(err?.message || "Unable to load profile.");
        setProfile(null);
      })
      .finally(() => setLoading(false));
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSaving(true);
    try {
      const updated = await updateProfile({
        given_name: form.given_name.trim(),
        family_name: form.family_name.trim(),
        phone_number: form.phone_number.trim(),
      });
      setProfile(updated);
      setForm({
        given_name: updated.given_name ?? "",
        family_name: updated.family_name ?? "",
        phone_number: updated.phone_number ?? "",
      });
      setSuccess("Profile updated.");
    } catch (err) {
      setError(err?.message || "Unable to update profile.");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = () => {
    onClose();
    onOpenChangePassword?.();
  };

  const handleClose = () => {
    setError(null);
    setSuccess(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="my-profile-title"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 id="my-profile-title" className="text-xl font-semibold text-slate-800">
              My profile
            </h2>
            <button
              onClick={handleClose}
              className="text-slate-400 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 rounded"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {loading && (
            <p className="text-sm text-slate-600">Loading profile…</p>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md animate-notice-slide-in">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md animate-notice-slide-in">
              <p className="text-sm text-green-800">{success}</p>
            </div>
          )}

          {!loading && profile && (
            <form onSubmit={handleSubmit}>
              <label htmlFor="my-profile-email" className="block text-sm font-medium text-slate-700 mb-1">
                Email
              </label>
              <input
                id="my-profile-email"
                type="email"
                value={profile.email ?? ""}
                readOnly
                className="w-full px-3 py-2 border border-slate-300 rounded-md bg-slate-50 text-slate-600 mb-4"
                aria-label="Email (read-only)"
              />

              <label htmlFor="my-profile-given-name" className="block text-sm font-medium text-slate-700 mb-1">
                First name
              </label>
              <input
                id="my-profile-given-name"
                type="text"
                value={form.given_name}
                onChange={(e) => setForm((f) => ({ ...f, given_name: e.target.value }))}
                maxLength={100}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2B7F8D] focus:border-[#2B7F8D] mb-4"
                placeholder="First name"
                aria-label="First name"
              />

              <label htmlFor="my-profile-family-name" className="block text-sm font-medium text-slate-700 mb-1">
                Last name
              </label>
              <input
                id="my-profile-family-name"
                type="text"
                value={form.family_name}
                onChange={(e) => setForm((f) => ({ ...f, family_name: e.target.value }))}
                maxLength={100}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2B7F8D] focus:border-[#2B7F8D] mb-4"
                placeholder="Last name"
                aria-label="Last name"
              />

              <label htmlFor="my-profile-phone" className="block text-sm font-medium text-slate-700 mb-1">
                Phone number
              </label>
              <input
                id="my-profile-phone"
                type="tel"
                value={form.phone_number}
                onChange={(e) => setForm((f) => ({ ...f, phone_number: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2B7F8D] focus:border-[#2B7F8D] mb-4"
                placeholder="+1 555 123 4567"
                aria-label="Phone number (E.164)"
              />
              <p className="text-xs text-slate-500 mb-4">
                Use E.164 format (e.g. +15551234567) or leave blank.
              </p>

              <div className="flex flex-col gap-3">
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 px-4 py-2 text-sm border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 px-4 py-2 text-sm bg-[#2B7F8D] text-white rounded-md hover:bg-[#256f7a] focus:outline-none focus:ring-2 focus:ring-[#2B7F8D] focus:ring-offset-2 disabled:opacity-50"
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                </div>
                {onOpenChangePassword && (
                  <button
                    type="button"
                    onClick={handleChangePassword}
                    className="w-full px-4 py-2 text-sm border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#2B7F8D] focus:ring-offset-2"
                  >
                    Change password
                  </button>
                )}
              </div>
            </form>
          )}

          {!loading && !profile && !error && (
            <p className="text-sm text-slate-600">No profile data.</p>
          )}
        </div>
      </div>
    </div>
  );
}
